/** import package [start] */
import { useImperativeHandle, forwardRef, useState, createRef, useEffect, Dispatch, SetStateAction } from 'react';
/** import package [end] */

/** import custom dependency [start] */
import { addBorderColor } from '../../../../utils/helpers';
/** import custom dependency [end] */

export interface IFRef {
    getJWTName: () => string;
    setJWTName: (name: string) => void;
    isNameValid: () => boolean;

    getJWTKey: () => string;
    setJWTKey: (key: string) => void;
    isKeyValid: () => boolean;
}

/** type declaration for End Point JWT security props */
export interface JWTSecurityProps {
    isEditMode: boolean;
    jwtName: string;
    setJWTName: Dispatch<SetStateAction<string>>;
    jwtKey: string;
    setJWTKey: Dispatch<SetStateAction<string>>;
}

/**
 * JWT Security component
 * @returns JSX.Element
 */

const JWTSecurity = forwardRef<IFRef, JWTSecurityProps>(
    ({ isEditMode, jwtKey, jwtName, setJWTKey, setJWTName }, ref) => {
        //misc states
        const [componentError, setComponentError] = useState<string | null>(null);

        //component dependent states
        const [valid, setValid] = useState<{ name: boolean; key: boolean }>({ name: false, key: false });

        //refs
        const jwtNameRef = createRef<HTMLInputElement>();
        const jwtKeyRef = createRef<HTMLTextAreaElement>();

        //exposed states and functions
        useImperativeHandle(ref, () => {
            return {
                getJWTName: () => jwtName,
                setJWTName: (name) => setJWTName(name),
                isNameValid: () => valid.name,
                getJWTKey: () => jwtKey,
                setJWTKey: (key) => setJWTKey(key),
                isKeyValid: () => valid.key,
            };
        });

        /**
         * to validate the JWT name
         * @returns void
         */
        const validateName = (): void => {
            let res = false;

            if (jwtName === '') {
                setComponentError('JWT public key name should not be null');
                addBorderColor(jwtNameRef, 'red');

                res = false;
            } else if (jwtName.length < 3 || jwtName.length > 20) {
                setComponentError('JWT public key name should have a length between 3 and 20');
                addBorderColor(jwtNameRef, 'red');

                res = false;
            } else {
                setComponentError(null);
                addBorderColor(jwtNameRef);

                res = true;
            }

            if (!isEditMode) {
                addBorderColor(jwtNameRef);
                setComponentError(null);
            }

            setValid((prev) => {
                return {
                    ...prev,
                    name: res,
                };
            });
        };

        /**
         * to validate the JWT key
         * @returns void
         */
        const validateKey = (): void => {
            let res = false;

            if (jwtKey === '') {
                setComponentError('Please fill JWT public key field.');
                addBorderColor(jwtKeyRef, 'red');

                res = false;
            } else if (jwtKey.length < 170 || jwtKey.length > 800) {
                setComponentError('JWT public key should have a length between 170 and 800.');
                addBorderColor(jwtKeyRef, 'red');

                res = false;
            } else {
                setComponentError(null);
                addBorderColor(jwtKeyRef);

                res = true;
            }

            if (!isEditMode) {
                addBorderColor(jwtKeyRef);
                setComponentError(null);
            }

            setValid((prev) => {
                return {
                    ...prev,
                    key: res,
                };
            });
        };

        // will run the following function on name change
        useEffect(() => {
            validateName();
        }, [jwtName]);

        // will run the following function on key change
        useEffect(() => {
            validateKey();
        }, [jwtKey]);

        /** Event handlers [start] */

        /**
         * Input handler for JWT Name input
         * @param name
         * @return void
         */
        const onNameChangeHandler = (name: string) => {
            setJWTName(name);
        };

        /**
         * Input handler for JWT key input
         * @param key
         * @return void
         */
        const onKeyChangeHandler = (key: string): void => {
            setJWTKey(key);
        };

        /** Event handlers [end] */

        return (
            <>
                <div className='mb-2 row editinput' id='editinput4'>
                    <label htmlFor='jwt-key-name' className='col-sm-12 col-md-4 col-lg-2 col-xl-2 col-form-label col-6'>
                        JWT Public Key Name
                        <span className='text-danger'>*</span>
                    </label>
                    <div className='col-sm-12 col-md-6 col-xl-4 col-lg-5 col-12'>
                        <input
                            type='text'
                            className='form-control'
                            name='JwtKeyName'
                            id='jwt-key-name'
                            value={jwtName}
                            disabled={!isEditMode}
                            autoComplete='off'
                            onChange={(e) => {
                                onNameChangeHandler(e.target.value);
                            }}
                            ref={jwtNameRef}
                        />
                    </div>
                </div>
                <div className='mb-3 row editinput' id='editinput2'>
                    <label htmlFor='jwt-key' className='col-sm-12 col-md-4 col-lg-2 col-xl-2 col-form-label col-6'>
                        JWT Public Key
                        <span className='text-danger'>*</span>
                        <i
                            title='Restrict API usage to requests that include a valid JWT. Public key can be generated using RSA and ECDSA algorithms only.'
                            className='fa-solid fa-circle-info ms-1'
                        />
                    </label>

                    <div className='col-sm-12 col-md-6 col-xl-4 col-lg-5 col-12'>
                        <textarea
                            rows={4}
                            cols={50}
                            className='form-control'
                            name='JwtKey'
                            id='jwt-key'
                            placeholder={isEditMode ? '-----BEGIN PUBLIC KEY----------END PUBLIC KEY-----' : ''}
                            value={jwtKey}
                            disabled={!isEditMode}
                            autoComplete='off'
                            onChange={(e) => {
                                onKeyChangeHandler(e.target.value);
                            }}
                            ref={jwtKeyRef}
                        />
                    </div>
                </div>
                {componentError && (
                    <div className='text-center'>
                        <p className='text-danger py-2'>{componentError}</p>
                    </div>
                )}
            </>
        );
    },
);

JWTSecurity.displayName = 'JWTSecurity';

export default JWTSecurity;
